import React, { useMemo, useState, useCallback } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import {
  IDENT_SUGGESTION_GROUP_LABELS,
  buildChatbotIdentifierSuggestions,
} from "../../utils/chatbotIdentifierSuggestions";

function parseIdentificadores(str) {
  if (!str) return [];
  return String(str)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

const filter = createFilterOptions({
  limit: 200,
  stringify: (option) =>
    [
      option?.value,
      option?.label,
      option?.hint,
      IDENT_SUGGESTION_GROUP_LABELS[option?.group],
    ]
      .filter(Boolean)
      .join(" "),
});

export default function ChatbotIdentificadoresInput({
  value,
  onChange,
  disabled,
  slot,
  allSlots = [],
  slotNome = "",
}) {
  const [input, setInput] = useState("");
  const chips = useMemo(() => parseIdentificadores(value), [value]);

  const suggestions = useMemo(
    () =>
      buildChatbotIdentifierSuggestions({
        slot,
        allSlots,
        slotNome,
        selected: chips,
        inputFilter: input,
      }),
    [slot, allSlots, slotNome, chips, input]
  );

  const commitFreeText = useCallback(
    (raw) => {
      const val = String(raw || "").trim();
      if (!val || chips.includes(val)) return;
      onChange([...chips, val].join(","));
    },
    [chips, onChange]
  );

  const handleChange = useCallback(
    (_, newValue) => {
      const normalized = (newValue || [])
        .map((item) => (typeof item === "string" ? item : item?.value))
        .map((item) => String(item || "").trim())
        .filter(Boolean);

      const unique = [];
      const seen = new Set();
      for (const item of normalized) {
        const key = item.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push(item);
      }

      onChange(unique.join(","));
      setInput("");
    },
    [onChange]
  );

  return (
    <Box>
      <Autocomplete
        multiple
        freeSolo
        disabled={disabled}
        options={suggestions}
        value={chips}
        inputValue={input}
        onInputChange={(_, next, reason) => {
          if (reason === "reset") return;
          setInput(next);
        }}
        onChange={handleChange}
        filterOptions={(options, params) => filter(options, params)}
        getOptionLabel={(option) => (typeof option === "string" ? option : option.value || "")}
        isOptionEqualToValue={(option, val) => {
          const a = typeof option === "string" ? option : option?.value;
          return String(a || "").toLowerCase() === String(val || "").toLowerCase();
        }}
        groupBy={(option) =>
          typeof option === "string" ? "" : IDENT_SUGGESTION_GROUP_LABELS[option.group] || ""
        }
        renderOption={(props, option) => {
          if (typeof option === "string") {
            return <li {...props}>{option}</li>;
          }
          const showValue = option.value !== option.label;
          return (
            <li {...props} key={`${option.group}:${option.value}`}>
              <Box sx={{ py: 0.25, maxWidth: "100%" }}>
                <Typography variant="body2" noWrap>
                  {option.label}
                </Typography>
                {showValue ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: "monospace" }}
                    noWrap
                    display="block"
                  >
                    {option.value}
                  </Typography>
                ) : null}
                {option.hint ? (
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {option.hint}
                  </Typography>
                ) : null}
              </Box>
            </li>
          );
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((chip, index) => (
            <Chip {...getTagProps({ index })} key={chip} label={chip} size="small" />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Identificador"
            size="small"
            placeholder="Ex.: Sim, Comprar passagem, cadastro..."
            onKeyDown={(e) => {
              if (e.key === "," && input.trim()) {
                e.preventDefault();
                commitFreeText(input);
                setInput("");
              } else if (e.key === "Backspace" && !input && chips.length) {
                onChange(chips.slice(0, -1).join(","));
              }
            }}
          />
        )}
        noOptionsText={input.trim() ? "Pressione Enter para usar o texto digitado" : "Nenhuma sugestão"}
        openOnFocus
        slotProps={{
          popper: { sx: { zIndex: (theme) => theme.zIndex.modal + 2 } },
          listbox: { sx: { maxHeight: 320 } },
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
        O que o cliente precisa dizer ou escolher para chegar neste passo. Deixe em branco para aceitar
        qualquer resposta. Para regras mais específicas (ex.: cadastro igual a sim), use{" "}
        <strong>Condições</strong> abaixo.
      </Typography>
    </Box>
  );
}
